import keymirror from 'keymirror';
import { request } from 'js/api/axios';
import { callApi, noSchemaQuerySuccessMutator } from '../api';
import { SCHEMAS } from 'js/api/schemas';
import { spreadApiKeys, extractApiKeys } from '../utils';

export const types = keymirror({
  ...spreadApiKeys('LOAD_RUBRIC'),
  ...spreadApiKeys('LOAD_RUBRIC_AVAILABLE_SOURCES'),
  ...spreadApiKeys('LOAD_RUBRIC_TEAM_SCORES'),
  ...spreadApiKeys('LOAD_WORKSPACE_RUBRICS'),
  ...spreadApiKeys('LOAD_TEAM_RUBRICS'),
  ...spreadApiKeys('LOAD_TEAM_RUBRIC'),
  ...spreadApiKeys('UPDATE_TEAM_RUBRIC'),
});

export function load(rubricId) {
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LOAD_RUBRIC, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/rubrics/${encodeURIComponent(
              rubricId
            )}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data: { rubric_id: rubricId },
        schema: SCHEMAS.RUBRIC,
      },
      dispatch
    );
  };
}

export function update(rubricId, attrs) {
  return (dispatch) => {
    const patchPayload = { id: rubricId, ...attrs };
    return callApi(
      {
        types: extractApiKeys(types.LOAD_RUBRIC, types),
        request: (data, token, currentWorkspaceId) =>
          request.put(
            `/api/workspaces/${currentWorkspaceId}/rubrics/${encodeURIComponent(
              rubricId
            )}`,
            data,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data: { rubric: attrs },
        meta: { patch: { rubrics: { [rubricId]: patchPayload } } },
        schema: SCHEMAS.RUBRIC,
      },
      dispatch
    );
  };
}

export function loadAvailableSources(queryId = null) {
  const meta = { queryId };
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LOAD_RUBRIC_AVAILABLE_SOURCES, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/rubrics/available-sources`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

/** Loads team rubric scores into `state.queries[queryId]` (no entities). */
export function loadTeamScores(rubricId, teamId, queryId, options = {}) {
  const { omitUnweighted = false, weightScheme = '' } = options;
  const meta = { queryId };
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LOAD_RUBRIC_TEAM_SCORES, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/teams/${teamId}/rubrics/${encodeURIComponent(
              rubricId
            )}/scores`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              params: {
                ...(omitUnweighted ? { omit_unweighted: true } : {}),
                ...(String(weightScheme ?? '').trim() !== ''
                  ? { weight_scheme: String(weightScheme).trim() }
                  : {}),
              },
            }
          ),
        data: { rubric_id: rubricId, team_id: teamId },
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

/** Loads workspace rubric rows into `state.queries[queryId]` (no entities). */
export function listWorkspaceRubrics(workspaceId, queryId = null) {
  const meta = { queryId };
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LOAD_WORKSPACE_RUBRICS, types),
        request: (_data, token) =>
          request.get(`/api/workspaces/${workspaceId}/rubrics`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        data: {},
        meta,
        schema: SCHEMAS.RUBRIC_ARRAY,
      },
      dispatch
    );
  };
}

/** Loads `{ is_favorite, weight_scheme }` for one team/rubric pair into `state.queries[queryId]`. */
export function loadTeamRubric(teamId, rubricId, queryId = null) {
  const meta = { queryId };
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LOAD_TEAM_RUBRIC, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/teams/${teamId}/rubrics/${encodeURIComponent(
              rubricId
            )}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data: { team_id: teamId, rubric_id: rubricId },
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

export function listTeamRubrics(teamId, queryId = null) {
  const meta = { queryId };
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.LOAD_TEAM_RUBRICS, types),
        request: (_data, token, currentWorkspaceId) =>
          request.get(
            `/api/workspaces/${currentWorkspaceId}/teams/${teamId}/rubrics`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data: { team_id: teamId },
        meta,
        mutators: [undefined, noSchemaQuerySuccessMutator(meta), undefined],
      },
      dispatch
    );
  };
}

function pickTeamRubricPatch(attrs) {
  const out = {};
  if (Object.prototype.hasOwnProperty.call(attrs, 'is_favorite')) {
    out.is_favorite = attrs.is_favorite;
  }
  if (Object.prototype.hasOwnProperty.call(attrs, 'weight_scheme')) {
    out.weight_scheme = attrs.weight_scheme;
  }
  return out;
}

/**
 * Creates or updates `teams_rubrics` for this team and rubric. Include only fields to change,
 * e.g. `{ is_favorite: true }`, `{ weight_scheme: schemeIdOrNull }`, or both in one request.
 */
export function updateTeamRubric(teamId, rubricId, teamRubricAttributes) {
  const team_rubric = pickTeamRubricPatch(teamRubricAttributes);
  return (dispatch) => {
    return callApi(
      {
        types: extractApiKeys(types.UPDATE_TEAM_RUBRIC, types),
        request: (_data, token, currentWorkspaceId) =>
          request.patch(
            `/api/workspaces/${currentWorkspaceId}/teams/${teamId}/rubrics/${encodeURIComponent(
              rubricId
            )}`,
            { team_rubric },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        data: { team_id: teamId, rubric_id: rubricId },
      },
      dispatch
    );
  };
}
